/**
 * The Conta field's leading globe and trailing check (frame 86:2 `Campo/Conta`): a pasted
 * platform URL is visibly recognised before the form is submitted. Pure string derivation, run on
 * every keystroke -- no network call.
 *
 * Two patterns, not one: the globe fires once the host alone names a platform (`instagram.com`,
 * no path yet); the check needs the handle after it too, same regex `AddAccount`'s own
 * `parseAccount` matches to build the payload. A host that names neither platform renders neither
 * icon, rather than a globe promising a resolution the check can't confirm.
 */
import { Check, Globe } from 'lucide-react';
import type { ReactNode } from 'react';

import { Input } from '../../../ui/input';
import { cn } from '../../../ui/utils';
import type { Platform } from './api/schemas';

const ACCOUNT_HOST = /^(?:https?:\/\/)?(?:www\.)?(instagram|tiktok)\.com(?:\/|$)/i;
const ACCOUNT_URL = /^(?:https?:\/\/)?(?:www\.)?(instagram|tiktok)\.com\/@?[^/?#]+/i;

function hostPlatform(raw: string): Platform | null {
  const match = raw.trim().match(ACCOUNT_HOST);
  return match ? (match[1]!.toLowerCase() as Platform) : null;
}

function resolvedPlatform(raw: string): Platform | null {
  const match = raw.trim().match(ACCOUNT_URL);
  return match ? (match[1]!.toLowerCase() as Platform) : null;
}

export function AccountUrlField(props: {
  id: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  'aria-invalid'?: true;
  'aria-describedby'?: string;
}): ReactNode {
  const { id, value, placeholder, onChange, ...controlProps } = props;
  const showHost = hostPlatform(value) !== null;
  const showResolved = resolvedPlatform(value) !== null;
  return (
    <div className="relative">
      {showHost && (
        <Globe
          aria-hidden="true"
          data-testid="account-url-host-icon"
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
        />
      )}
      <Input
        id={id}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={cn(showHost && 'pl-9', showResolved && 'pr-9')}
        {...controlProps}
      />
      {showResolved && (
        <Check
          aria-hidden="true"
          data-testid="account-url-resolved-icon"
          className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-primary"
        />
      )}
    </div>
  );
}
